import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CalendarCheck, CalendarX, Crown, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pad = (n) => String(n).padStart(2, "0");
const toKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const buildDays = (count = 12) => {
  const days = [];
  const d = new Date();
  while (days.length < count) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0) days.push(new Date(d));
  }
  return days;
};

const prettyDate = (key) =>
  new Date(`${key}T12:00:00`).toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

const Panel = ({ children, testid }) => (
  <div className="flex min-h-screen items-center justify-center bg-obsidian px-6 py-20">
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="gold-border-glow w-full max-w-2xl bg-wine/70 p-8 sm:p-12"
      data-testid={testid}
    >
      <Link to="/" className="mb-8 flex items-center gap-3" data-testid="manage-brand-link">
        <span className="flex h-9 w-9 items-center justify-center border border-gold/40 bg-royal/60 text-gold">
          <Crown size={16} strokeWidth={1.5} />
        </span>
        <span className="font-display text-sm font-semibold tracking-[0.22em] text-white">
          VISION ARIES
        </span>
      </Link>
      {children}
    </motion.div>
  </div>
);

export const BookingManage = () => {
  const { token } = useParams();
  const [booking, setBooking] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [mode, setMode] = useState("view"); // view | reschedule | confirm-cancel | cancelled
  const [days] = useState(buildDays);
  const [date, setDate] = useState(toKey(days[0]));
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/bookings/manage/${token}`);
        setBooking(res.data);
        if (res.data.status === "cancelled") setMode("cancelled");
      } catch {
        setNotFound(true);
      }
    };
    load();
  }, [token]);

  useEffect(() => {
    if (mode !== "reschedule") return;
    const load = async () => {
      setTime(null);
      try {
        const res = await axios.get(`${API}/availability`, { params: { date } });
        setSlots(res.data.slots);
      } catch {
        setSlots([]);
      }
    };
    load();
  }, [mode, date]);

  const reschedule = async () => {
    if (!time) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/bookings/manage/${token}/reschedule`, { date, time });
      setBooking(res.data);
      setMode("view");
      toast.success("Consultation rescheduled", {
        description: `${prettyDate(res.data.date)} at ${res.data.time} ET`,
      });
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.error("That slot was just taken", { description: "Please pick another time." });
      } else {
        toast.error("Could not reschedule", { description: "Please call (770) 912-1516." });
      }
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/bookings/manage/${token}/cancel`);
      setMode("cancelled");
      toast.success("Consultation cancelled");
    } catch {
      toast.error("Could not cancel", { description: "Please call (770) 912-1516." });
    } finally {
      setBusy(false);
    }
  };

  if (notFound) {
    return (
      <Panel testid="manage-not-found">
        <h1 className="font-display text-2xl font-semibold text-white">Booking not found.</h1>
        <p className="mt-4 text-sm leading-relaxed text-white/55">
          This link may be old or incorrect. Call{" "}
          <a href="tel:+17709121516" className="text-gold">(770) 912-1516</a> and we’ll sort it out.
        </p>
      </Panel>
    );
  }

  if (!booking) {
    return (
      <Panel testid="manage-loading">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/50">
          Loading your booking…
        </p>
      </Panel>
    );
  }

  if (mode === "cancelled") {
    return (
      <Panel testid="manage-cancelled">
        <span className="flex h-14 w-14 items-center justify-center border border-gold/40 bg-gold/10 text-gold">
          <CalendarX size={24} strokeWidth={1.5} />
        </span>
        <h1 className="mt-6 font-display text-2xl font-semibold text-white">Consultation cancelled.</h1>
        <p className="mt-4 text-sm leading-relaxed text-white/55">
          Your slot has been released and a cancellation email is on its way. Whenever you’re ready,
          we’d be honored to reschedule — call{" "}
          <a href="tel:+17709121516" className="text-gold">(770) 912-1516</a>.
        </p>
      </Panel>
    );
  }

  return (
    <Panel testid="manage-booking-card">
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Manage Your Consultation</p>
      <h1 className="mt-4 font-display text-2xl font-semibold text-white sm:text-3xl" data-testid="manage-current-slot">
        {prettyDate(booking.date)} · {booking.time} ET
      </h1>
      <p className="mt-3 text-sm text-white/55">
        {booking.service} with Roberta Rae Hare
      </p>

      {mode === "view" && (
        <div className="mt-10 flex flex-wrap gap-4">
          <button
            onClick={() => setMode("reschedule")}
            data-testid="manage-reschedule-button"
            className="flex items-center gap-3 bg-gold px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-obsidian transition-all duration-300 hover:bg-gold-light"
          >
            <RefreshCw size={14} /> Reschedule
          </button>
          <button
            onClick={() => setMode("confirm-cancel")}
            data-testid="manage-cancel-button"
            className="flex items-center gap-3 border border-white/20 px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-white/70 transition-all duration-300 hover:border-red-400/60 hover:text-red-300"
          >
            <CalendarX size={14} /> Cancel
          </button>
        </div>
      )}

      {mode === "confirm-cancel" && (
        <div className="mt-10 border border-red-400/30 bg-red-400/5 p-6" data-testid="manage-cancel-confirm-box">
          <p className="text-sm text-white/70">
            Cancel this consultation? Your slot will be released immediately.
          </p>
          <div className="mt-6 flex flex-wrap gap-4">
            <button
              onClick={cancel}
              disabled={busy}
              data-testid="manage-cancel-confirm"
              className="border border-red-400/60 px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] text-red-300 transition-all duration-300 hover:bg-red-400/10 disabled:opacity-50"
            >
              {busy ? "Cancelling…" : "Yes, cancel it"}
            </button>
            <button
              onClick={() => setMode("view")}
              data-testid="manage-cancel-back"
              className="font-mono text-xs uppercase tracking-[0.2em] text-white/50 transition-colors hover:text-gold"
            >
              Keep my booking
            </button>
          </div>
        </div>
      )}

      {mode === "reschedule" && (
        <div className="mt-10" data-testid="manage-reschedule-picker">
          <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">Pick a new day</p>
          <div className="flex gap-2 overflow-x-auto pb-2" data-testid="manage-day-list">
            {days.map((d) => {
              const key = toKey(d);
              const active = key === date;
              return (
                <button
                  key={key}
                  onClick={() => setDate(key)}
                  data-testid={`manage-day-${key}`}
                  className={`flex w-16 shrink-0 flex-col items-center border px-2 py-3 transition-all duration-300 ${
                    active
                      ? "border-gold bg-gold/15 text-gold"
                      : "border-white/15 text-white/50 hover:border-gold/40 hover:text-gold"
                  }`}
                >
                  <span className="font-mono text-[10px] uppercase tracking-widest">
                    {d.toLocaleDateString("en-US", { weekday: "short" })}
                  </span>
                  <span className="mt-1 font-display text-lg">{d.getDate()}</span>
                </button>
              );
            })}
          </div>
          <p className="mb-3 mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
            Available times · {prettyDate(date)}
          </p>
          {slots.length === 0 ? (
            <p className="text-sm text-white/40" data-testid="manage-no-slots">
              No availability this day — pick another.
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="manage-slot-list">
              {slots.map((s) => (
                <button
                  key={s.time}
                  disabled={!s.available}
                  onClick={() => setTime(s.time)}
                  data-testid={`manage-slot-${s.time.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                  className={`border px-3 py-3 font-mono text-xs tracking-widest transition-all duration-300 ${
                    !s.available
                      ? "cursor-not-allowed border-white/5 text-white/20 line-through"
                      : time === s.time
                        ? "border-gold bg-gold text-obsidian"
                        : "border-white/15 text-white/60 hover:border-gold/40 hover:text-gold"
                  }`}
                >
                  {s.time}
                </button>
              ))}
            </div>
          )}
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <button
              onClick={reschedule}
              disabled={!time || busy}
              data-testid="manage-reschedule-confirm"
              className="flex items-center gap-3 bg-gold px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-obsidian transition-all duration-300 hover:bg-gold-light disabled:cursor-not-allowed disabled:opacity-40"
            >
              <CalendarCheck size={14} /> {busy ? "Moving…" : time ? `Move to ${time}` : "Select a time"}
            </button>
            <button
              onClick={() => setMode("view")}
              data-testid="manage-reschedule-back"
              className="font-mono text-xs uppercase tracking-[0.2em] text-white/50 transition-colors hover:text-gold"
            >
              Back
            </button>
          </div>
        </div>
      )}
    </Panel>
  );
};
